import { makeAutoObservable } from 'mobx'

import { DisposeBag } from '@src/lib/dispose'

export type ConnectionType =
  | 'bluetooth'
  | 'cellular'
  | 'ethernet'
  | 'none'
  | 'mixed'
  | 'other'
  | 'unknown'
  | 'wifi'
  | 'wimax'

export type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | '4g' | 'unknown'

/**
 * A typed data structure for the `NetworkInformation` interface.
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
interface NetworkInformation extends EventTarget {
  /**
   * Returns the type of connection a device is using to communicate with the network.
   */
  readonly type: ConnectionType
  /**
   * Returns the effective type of the connection. This value is determined using a combination of recently observed round-trip time and downlink values.
   */
  readonly effectiveType: EffectiveConnectionType
  /**
   * Returns the estimated effective round-trip time of the current connection, rounded to the nearest multiple of 25 milliseconds.
   */
  readonly rtt: number
  /**
   * Returns true if the user has set a reduced data usage option on the user agent.
   */
  readonly saveData: boolean
  /**
   * Returns the maximum downlink speed, in megabits per second (Mbps), for the underlying connection technology.
   */
  readonly downlinkMax: number
  /**
   * Returns the effective bandwidth estimate in megabits per second, rounded to the nearest multiple of 25 kilobits per seconds.
   */
  readonly downlink: number
}

/**
 * A UI store for the `NetworkInformation` interface.
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export default class NetworkInformationUiStore {
  private _connectionType: ConnectionType = 'unknown'
  private _effectiveType: EffectiveConnectionType = 'unknown'
  private _rtt: number = -1
  private _downlinkMax: number = -1
  private _downlink: number = -1
  private _saveData: boolean = false
  private readonly disposeBag = new DisposeBag()

  constructor() {
    makeAutoObservable<this>(this, { tearDown: false }, { autoBind: true })

    this.disposeBag.add(this.registerNetworkInformationListener())
  }

  private handleNetworkInformationChange = () => {
    const connection = this.getConnection()
    if (!connection) {
      return
    }

    /**
     * network information type is not present in the browser payload as far as i can tell,
     * however it is present in the specification, so in the future when this becomes GA we will
     * have access to this property.
     */
    this._connectionType = connection.type ?? 'unknown'
    this._effectiveType = connection.effectiveType ?? 'unknown'
    this._rtt = connection.rtt
    this._saveData = connection.saveData
    this._downlinkMax = connection.downlinkMax
    this._downlink = connection.downlink
  }

  private registerNetworkInformationListener() {
    // connection is not supported in all browsers, so we will need to exit early
    // if the browser is firefox or safari.
    const connection = this.getConnection()
    if (!connection) {
      return () => {}
    }

    const abortController = new AbortController()
    this.handleNetworkInformationChange()

    connection.addEventListener('change', this.handleNetworkInformationChange, {
      signal: abortController.signal,
      capture: true,
    })

    return () => {
      abortController.abort()
    }
  }

  get connectionType() {
    return this._connectionType
  }

  get effectiveType() {
    return this._effectiveType
  }

  get rtt() {
    return this._rtt
  }

  get downlinkMax() {
    return this._downlinkMax
  }

  get downlink() {
    return this._downlink
  }

  get saveData() {
    return this._saveData
  }

  private getConnection() {
    if (!('connection' in navigator)) {
      return null
    }

    return navigator.connection as NetworkInformation
  }

  tearDown() {
    this.disposeBag.dispose()
  }
}
